import type { ReactNode } from 'react'
import type { LegalDoc } from './content'
import { Reveal } from '@gjl/motion/css'
import { blocksOf, levelOf, runsOf, textOf, type RichNode } from '@gjl/richtext'

/**
 * Per-region className overrides. The body markup (intro paragraphs, the
 * section map, the Reveal wiring, the "Last updated" line) was byte-identical
 * across every bespoke site's `LegalPage`; only these design-token classes and
 * the hero varied. A site passes its own tokens here and keeps its exact look;
 * the structure lives here once.
 */
export interface LegalArticleClasses {
  /** Outer `<section>` band (background, vertical padding). */
  section?: string
  /** Wrapper around the stacked sections (vertical rhythm, e.g. `space-y-10`). */
  sectionsWrap?: string
  /** Intro paragraphs (everything after `doc.intro[0]`, which goes in the hero). */
  intro?: string
  /** Section `<h2>` headings. */
  heading?: string
  /** Section body paragraphs. */
  body?: string
  /** The "Last updated" meta line. */
  meta?: string
}

const DEFAULTS: Required<LegalArticleClasses> = {
  section: 'py-16 md:py-20',
  sectionsWrap: 'space-y-10',
  intro: 'mb-5 text-base leading-relaxed',
  heading: 'font-display mb-3 text-2xl',
  body: 'mb-4 text-base leading-relaxed last:mb-0',
  meta: 'mt-14 border-t pt-6 text-sm',
}

export interface LegalArticleProps {
  /**
   * The GENERATED document, for a site whose org is not seeded yet.
   *
   * ⚠️ OPTIONAL SINCE 2026-09-04, AND THE ABSENT CASE IS NOW THE NORMAL ONE.
   * Every seeded org holds its legal copy in the CMS, so the 15 sites on
   * `legalPageParts` pass `blocks` and no `doc` at all — the generator has moved
   * to the SEED, which is where generating a first draft belongs. It stays here
   * for the sites that cannot be seeded: `demo-samuel-sparrow`, whose policy
   * derives from the live published payload and so cannot be frozen into a
   * document, and any org awaiting its first seed.
   *
   * Passing NEITHER renders the hero and an empty column. That is deliberate:
   * absent renders as absent, and a legal page with no content is a page the
   * client deleted.
   */
  doc?: LegalDoc
  /**
   * CMS-authored body. When present it REPLACES the generated `doc` body.
   *
   * ⚠️ THIS IS THE CLIENT'S COPY AND IT WINS.
   *
   * 🔴 "PRESENT" MEANS REAL COPY, NOT MERELY NON-EMPTY, AND GETTING THAT WRONG
   * REACHED PRODUCTION. A routing stub is a one-paragraph placeholder that is
   * structurally indistinguishable from a body, so it won this contest on two
   * live sites and served "Routing stub for /privacy" as the whole policy. The
   * callers — `useLegalBlocks` and `legalPageParts` — now filter it via
   * `isRoutingStub`, so what arrives here is either the client's copy or
   * nothing. This component deliberately does NOT re-check: one reader of the
   * sentinel, at the seam where the page is resolved.
   *
   * Rendered NATIVELY rather than converted back to `LegalDoc`: that type is
   * flat strings with no marks, lists or links, so a round trip would silently
   * discard a client's bold, bullet or hyperlink on the first read.
   */
  blocks?: unknown
  /** The site's own bespoke hero. `doc.intro[0]` is conventionally its lead line. */
  hero?: ReactNode
  classes?: LegalArticleClasses
}

/**
 * One CMS block, in the same vocabulary the generated document uses: an `h2`,
 * a paragraph, or a bullet item. Anything else the editor can produce renders
 * as a paragraph rather than disappearing — a legal page silently dropping a
 * clause is worse than one rendering it plainly.
 */
function LegalBlock({ node, c }: { node: RichNode; c: Required<LegalArticleClasses> }) {
  const runs = runsOf(node)
  const inline = runs.length
    ? runs.map((run, i) => {
        const href = run.href
        const body = run.italic ? <em key={i}>{run.text}</em> : run.text
        return href ? (
          <a key={i} href={href} className="underline">
            {body}
          </a>
        ) : (
          <span key={i}>{body}</span>
        )
      })
    : textOf(node)

  if (node.type === 'heading' || levelOf(node) !== undefined) return <h2 className={c.heading}>{inline}</h2>
  if (typeof node.type === 'string' && node.type.startsWith('bulletListItem'))
    return <li className={c.body}>{inline}</li>
  return <p className={c.body}>{inline}</p>
}

/**
 * Renders a shared `LegalDoc` (from `buildPrivacyPolicy` / `buildAccessibilityStatement`)
 * through a site's own typography. The copy is single-sourced in `@gjl/site-kit`;
 * now the render structure is too. Each site supplies its hero + token classes.
 */
/**
 * A CMS legal body, rendered natively.
 *
 * ⚠️ EXPORTED SO THERE IS ONE WALKER, NOT TWO. `demo-holy-trinity-covina` draws
 * legal pages through its own `.prose` column rather than `LegalArticle`, and
 * the obvious move — a second small map over the blocks — is exactly how this
 * repo ended up with four rich-text walkers sharing one silent defect
 * (`cms-editor-plan.md` §6.11). A site supplies markup; it does not re-read the
 * format.
 *
 * `skipFirst` because `LegalArticle` shows the opening paragraph in the hero and
 * would otherwise print it twice; a site whose masthead shows only the title
 * passes `false` and gets the whole document.
 */
export function LegalBlocks({
  blocks,
  classes,
  skipFirst = true,
}: {
  blocks: unknown
  classes?: LegalArticleClasses
  skipFirst?: boolean
}) {
  const c = { ...DEFAULTS, ...classes }
  const all = blocksOf(blocks)
  const body = skipFirst && all.length > 1 ? all.slice(1) : all
  return (
    <>
      {body.map((node, i) => (
        <LegalBlock key={i} node={node} c={c} />
      ))}
    </>
  )
}

export function LegalArticle({ doc, blocks, hero, classes }: LegalArticleProps) {
  const c = { ...DEFAULTS, ...classes }
  const cms = blocksOf(blocks)

  return (
    <>
      {hero}

      <section className={c.section}>
        <div className="mx-auto max-w-3xl px-6">
          {cms.length > 0 ? (
            <div className={c.sectionsWrap}>
              <LegalBlocks blocks={blocks} classes={classes} />
            </div>
          ) : doc ? (
            <>
              {doc.intro.slice(1).map((p, i) => (
                <p key={i} className={c.intro}>
                  {p}
                </p>
              ))}

              <div className={c.sectionsWrap}>
                {doc.sections.map((section) => (
                  <Reveal key={section.heading}>
                    <div>
                      <h2 className={c.heading}>{section.heading}</h2>
                      {section.body.map((p, i) => (
                        <p key={i} className={c.body}>
                          {p}
                        </p>
                      ))}
                    </div>
                  </Reveal>
                ))}
              </div>

              <p className={c.meta}>Last updated: {doc.lastUpdated}</p>
            </>
          ) : null}
        </div>
      </section>
    </>
  )
}
